import {
  clampPageNumber,
  clampPageSize,
  hasNextPage,
  hasPreviousPage,
  type IPage,
  type IPageRequest,
} from "@ticketon/rewards-core";

/** GraphQL variables of every paged rewards query (`pageNumber` is 1-based). */
export interface IPageVariables {
  pageNumber: number;
  pageSize: number;
}

/**
 * Maps a core {@link IPageRequest} to GraphQL pagination variables, clamping the
 * page number to ≥ 1 and the page size to 1–100 so the server never rejects
 * the request with `InvalidRewardsPagination`.
 */
export function toPageVariables(request: IPageRequest): IPageVariables {
  return {
    pageNumber: clampPageNumber(request.pageNumber),
    pageSize: clampPageSize(request.pageSize),
  };
}

/** Backend `PagedResultsDefinition<T>` shape as selected by every list document. */
export interface IConnection<T> {
  items: T[];
  count: number;
  pageNumber: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Reads a backend connection into a core {@link IPage}. Returns `undefined`
 * while there is no data (loading, skipped, or errored) so callers can tell
 * "not loaded yet" from "loaded and empty". The server's `hasNextPage` /
 * `hasPreviousPage` flags win; they are derived only if absent.
 */
export function readPage<T>(connection: IConnection<T> | null | undefined): IPage<T> | undefined {
  if (!connection) return undefined;
  return {
    items: connection.items,
    count: connection.count,
    pageNumber: connection.pageNumber,
    pageSize: connection.pageSize,
    hasNextPage: hasNextPage(connection),
    hasPreviousPage: hasPreviousPage(connection),
  };
}
